import React, {
  type ReactNode, useCallback, useMemo, useState,
} from 'react';
import { t } from 'i18next';

import { ArrowLeftLine } from '@datanomik/core-ui/icons';
import {
  Button, type ButtonVariant, cn, Dialog, DialogBody, DialogFooter, DialogHeader, DialogPanel, InsideSurfaceProvider, ProgressBar, Tooltip,
} from '@datanomik/core-ui';

import { useAppContext } from '@/contexts/app';

import { InsideModalProvider, ModalSlotsProvider } from './context';
import './styles.css';
interface ModalProps {
  actionConfirmClassName?: string;
  confirmVariant?: ButtonVariant;
  className?: string;
  children: ReactNode;
  title?: string;
  expand?: boolean;
  openModal: boolean;
  closeModal?: (...args: any[]) => any;
  backModal?: (...args: any[]) => any;
  primaryButton?: string;
  actionConfirm?: (...args: any[]) => any;
  icon?: Record<string, any>;
  backdropClose?: boolean;
  DialogProps?: Record<string, any>;
  DialogHeaderProps?: Record<string, any>;
  DialogActionsProps?: Record<string, any>;
  isLoadingKey?: string;
}

const Modal = ({
  className,
  openModal,
  closeModal,
  title,
  children,
  primaryButton,
  backModal,
  actionConfirm,
  backdropClose = true,
  DialogProps,
  DialogHeaderProps,
  DialogActionsProps,
  isLoadingKey = 'modal',
  actionConfirmClassName = '',
  confirmVariant = 'primary',
}: ModalProps) => {
  const { isLoading } = useAppContext();
  const [headerHost, setHeaderHost] = useState<HTMLElement | null>(null);
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null);
  const [footerRequests, setFooterRequests] = useState(0);

  const requestFooter = useCallback(() => {
    setFooterRequests((count) => count + 1);
    return () => setFooterRequests((count) => count - 1);
  }, []);

  const slots = useMemo(() => ({
    header: headerHost,
    footer: footerHost,
    requestFooter,
  }), [headerHost, footerHost, requestFooter]);

  const hasSlotFooter = footerRequests > 0;
  const hasFooter = Boolean(DialogActionsProps?.dialogActions || primaryButton || hasSlotFooter);

  const focusPanelOnOpen = (event) => {
    event.preventDefault();
    event.currentTarget?.focus?.({ preventScroll: true });
  };

  const {
    maxWidth, fullWidth: _fullWidth, PaperProps, scroll: _scroll,
    backdropClose: _dialogBackdropClose, ...restDialogProps
  } = DialogProps ?? {};
  const {
    text, subText, startAdornment, endAdornment, ...restHeaderProps
  } = DialogHeaderProps ?? {};

  return (
    <Dialog
      open={openModal}
      onOpenChange={(next) => {
        if (!next && backdropClose && closeModal) closeModal();
      }}
    >
      <DialogPanel
        className={cn('dn-modal-panel', className)}
        style={PaperProps?.sx}
        size={maxWidth === false ? 'auto' : (maxWidth ?? 'sm')}
        fullWidth={false}
        onOpenAutoFocus={focusPanelOnOpen}
        {...restDialogProps}
      >
        {(DialogHeaderProps || title || backModal || closeModal) && (
          <DialogHeader
            icon={(
              backModal && (
                <Tooltip label={t('Back')}>
                  <Button
                    onClick={closeModal}
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('Back')}
                  >
                    <ArrowLeftLine />
                  </Button>
                </Tooltip>
              )
            )}
            title={text ? t(text) : t(title)}
            description={subText}
            {...(startAdornment ? { icon: startAdornment } : {})}
            action={(
              <>
                {endAdornment}
                <div ref={setHeaderHost} data-slot="modal-header-actions" className="flex items-center gap-2 empty:hidden" />
              </>
            )}
            {...restHeaderProps}
          />
        )}

        <DialogBody>
          <ProgressBar size="xs" style={{ opacity: isLoading[isLoadingKey] ? 1 : 0 }} />
          <div data-slot="modal-content" className={cn('flex flex-col gap-5', !hasFooter && 'pb-6')}>
            <InsideModalProvider value>
              <ModalSlotsProvider value={slots}>
                <InsideSurfaceProvider>
                  {children}
                </InsideSurfaceProvider>
              </ModalSlotsProvider>
            </InsideModalProvider>
          </div>
        </DialogBody>

        {hasFooter && (
          <DialogFooter>
            {hasSlotFooter && <div ref={setFooterHost} data-slot="modal-footer-actions" className="contents" />}
            {DialogActionsProps?.dialogActions}
            {primaryButton && (
              <>
                <Button
                  variant="outline"
                  onClick={closeModal}
                  className="flex-1"
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={actionConfirm}
                  variant={confirmVariant}
                  autoFocus
                  className={cn('flex-1', actionConfirmClassName)}
                  disabled={isLoading[isLoadingKey] || !actionConfirm}
                >
                  {t(primaryButton)}
                </Button>
              </>
            )}
          </DialogFooter>
        )}

      </DialogPanel>
    </Dialog>
  );
};
export default Modal;
